import { Fragment, useEffect, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChevronDown, ChevronRight, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Panel } from "@/components/KpiCard";
import { api, cleanParams, download, errMsg, money, moisLabel } from "@/lib/api";

const COULEURS = ["#047857", "#0369A1", "#D97706", "#7C3AED", "#BE185D", "#0D9488", "#65A30D", "#475569"];
const COMMUN = "Commun / non attribué";
const ton = (v) => (v < 0 ? "text-red-600" : "text-emerald-700");
const slug = (s) => s.normalize("NFD").replace(/[^\w]+/g, "-").toLowerCase();
const axe = { fontSize: 11, fill: "#64748b" };

const Courbes = ({ data }) => {
  const lignes = data.immeubles.filter((i) => i.immeuble !== COMMUN);
  const pts = data.mois.map((m) => Object.fromEntries([["label", moisLabel(m)], ...lignes.map((i) => [i.immeuble, i.mois[m]?.solde ?? 0])]));
  return (
    <div className="h-72 mb-6" data-testid="solde-immeubles-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={pts} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={axe} />
          <YAxis tick={axe} tickFormatter={(v) => `${Math.round(v / 1000)} k$`} width={52} />
          <ReferenceLine y={0} stroke="#94a3b8" />
          <Tooltip formatter={(v) => money(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {lignes.map((i, k) => <Line key={i.immeuble} type="monotone" dataKey={i.immeuble} stroke={COULEURS[k % COULEURS.length]} strokeWidth={2} dot={{ r: 3 }} />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const Cellule = ({ x, testId }) => <td className={`py-2 px-2 text-right tabular whitespace-nowrap ${x ? ton(x.solde) : "text-slate-300"}`} data-testid={testId}>{x ? money(x.solde) : "—"}</td>;

const Detail = ({ i, mois }) => ["revenus", "depenses"].map((k) => (
  <tr key={k} className="bg-slate-50/60 text-xs text-slate-500" data-testid={`solde-imm-${slug(i.immeuble)}-${k}`}>
    <td className="py-1 pl-8 pr-2 sticky left-0 bg-slate-50">{k === "revenus" ? "Revenus" : "Dépenses"}</td>
    {mois.map((m) => <td key={m} className="py-1 px-2 text-right tabular whitespace-nowrap">{i.mois[m] ? money(i.mois[m][k]) : "—"}</td>)}
    <td className="py-1 px-2 text-right tabular whitespace-nowrap">{money(i.total[k])}</td>
  </tr>
));

const Exports = ({ mois }) => {
  const [detail, setDetail] = useState(true);
  const [dl, setDl] = useState("");
  const exporter = async (fmt) => {
    setDl(fmt);
    const nom = `solde_par_immeuble_${mois && mois !== "all" ? mois : "toutes-periodes"}${detail ? "_detail" : ""}.${fmt === "excel" ? "xlsx" : "pdf"}`;
    try { await download(`/dashboard/solde-immeubles/${fmt}`, cleanParams({ mois, detail }), nom); } catch (e) { toast.error(errMsg(e)); } finally { setDl(""); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer"><Checkbox checked={detail} onCheckedChange={(v) => setDetail(!!v)} data-testid="solde-imm-detail-checkbox" />Avec le détail des opérations</label>
      <Button size="sm" variant="outline" disabled={!!dl} onClick={() => exporter("pdf")} data-testid="solde-imm-export-pdf">{dl === "pdf" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1" />}PDF</Button>
      <Button size="sm" variant="outline" disabled={!!dl} onClick={() => exporter("excel")} data-testid="solde-imm-export-excel">{dl === "excel" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />}Excel</Button>
    </div>
  );
};

export const SoldeImmeublesPanel = ({ mois }) => {
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState({});
  useEffect(() => {
    api.get("/dashboard/solde-immeubles", { params: cleanParams({ mois }) }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [mois]);
  const th = "py-2 px-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 whitespace-nowrap";
  return (
    <Panel title="Solde du mois par immeuble" testId="solde-immeubles" action={<Exports mois={mois} />}>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700" /> : !data.immeubles.length ? <p className="text-sm text-slate-500">Aucune opération.</p> : (
        <>
          <Courbes data={data} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="solde-immeubles-table">
              <thead><tr><th className={`${th} text-left sticky left-0 bg-white`}>Immeuble</th>{data.mois.map((m) => <th key={m} className={`${th} text-right`}>{moisLabel(m)}</th>)}<th className={`${th} text-right`}>Total</th></tr></thead>
              <tbody>
                {data.immeubles.map((i) => (
                  <Fragment key={i.immeuble}>
                    <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOuvert({ ...ouvert, [i.immeuble]: !ouvert[i.immeuble] })} data-testid={`solde-imm-row-${slug(i.immeuble)}`}>
                      <td className={`py-2 px-2 font-medium whitespace-nowrap sticky left-0 bg-white ${i.immeuble === COMMUN ? "text-slate-500 italic" : ""}`}>
                        {ouvert[i.immeuble] ? <ChevronDown className="inline h-3.5 w-3.5 mr-1" /> : <ChevronRight className="inline h-3.5 w-3.5 mr-1" />}{i.immeuble}
                      </td>
                      {data.mois.map((m) => <Cellule key={m} x={i.mois[m]} testId={`solde-imm-${slug(i.immeuble)}-${m}`} />)}
                      <td className={`py-2 px-2 text-right tabular font-semibold whitespace-nowrap ${ton(i.total.solde)}`} data-testid={`solde-imm-${slug(i.immeuble)}-total`}>{money(i.total.solde)}</td>
                    </tr>
                    {ouvert[i.immeuble] && <Detail i={i} mois={data.mois} />}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-slate-500">Rattachement : logement ou immeuble choisi dans Commentaires opérations, sinon immeuble reconnu dans la description (nom ou adresse, voir Paramètres › Immeubles) ; dépôts Airbnb/Booking selon le logement des réservations rapprochées. Cliquez sur une ligne pour voir revenus et dépenses.</p>
        </>
      )}
    </Panel>
  );
};
